import React, { useMemo } from "react";
import { RouteComponentProps } from "react-router";
import { NumberParam, StringParam, useQueryParams } from "use-query-params";
import { InsertGroupDialog } from "../components/groups/InsertGroupDialog";
import { GroupIcon, DeleteIcon, EditIcon } from "../components/icons";
import SortAscendingDirectionIcon from "../components/icons/SortAscendingDirectionIcon";
import SortDescendingDirectionIcon from "../components/icons/SortDescendingDirectionIcon";
import List from "../components/utils/List";
import Pagination from "../components/utils/Pagination";
import SearchInput from "../components/utils/SearchInput";
import {
  Badge,
  Button,
  ButtonGroup,
  Container,
  EmptyState,
  H6,
  Link,
  ScreenContainer,
  ScreenHeader,
} from "../components/wombat-ui";
import useGetGroups, {
  GET_GROUPS,
} from "../graphql/hooks/queries/useGetGroups";
import { Group, SortableVariables } from "../graphql/types";
import useDeleteGroup from "../hooks/useDeleteGroup";
import useTranslations from "../hooks/useTranslations";
import useViewer from "../hooks/useViewer";
import { getGroupColor } from "../utils/colors";

type GroupListProps = RouteComponentProps<{ id: string }>;

const GroupItem = ({ group, deleteGroup, setNewGroupDialog }) => {
  const t = useTranslations();
  return (
    <div
      key={group.id}
      id={`group_${group.id}}`}
      className="grid items-center grid-cols-12 py-4 pt-2 border-b"
    >
      <div className="col-span-3">
        <Link
          to={`/users?groupId=${group.id}`}
          id={`${group.name}-link`}
          className="text-sm font-medium truncate"
        >
          <p className="text-sm">{group.name}</p>
        </Link>
      </div>
      <div className="col-span-6">
        <p className="text-xs">{group.description}</p>
      </div>
      <div className="col-span-2">
        <span className="flex items-center space-x-4">
          <Badge variant={getGroupColor(group?.type)}>
            {t(`groups.types.${group.type}` as const)}
          </Badge>
        </span>
      </div>
      <div className="flex col-span-1">
        {group.canInsert && (
          <Button
            id={`group_edit_${group.id}`}
            variant="ghost"
            onClick={() => setNewGroupDialog({ ...group })}
          >
            <EditIcon iconClassName="w-5 h-5" />
          </Button>
        )}
        {group.canDestroy && (
          <Button
            variant="ghost"
            id={`group_delete_${group.id}`}
            onClick={() => {
              if (confirm(t("groups.confirmDelete")))
                deleteGroup({
                  variables: { input: { id: group.id } },
                });
            }}
          >
            <DeleteIcon iconClassName="w-5 h-5" />
          </Button>
        )}
      </div>
    </div>
  );
};

const GroupList: React.FC<GroupListProps> = ({ match }) => {
  const t = useTranslations();
  const { isCapableOfInsertingGroups, isCapableOfReadingGroups } = useViewer();
  const [newGroupDialog, setNewGroupDialog] = React.useState(undefined);

  const [deleteGroup] = useDeleteGroup({
    refetchQueries: [{ query: GET_GROUPS }],
  });

  const [{ page, q, sortBy, direction }, setQuery] = useQueryParams({
    page: NumberParam,
    sortBy: StringParam,
    direction: StringParam,
    q: StringParam,
    groupId: StringParam,
  });

  const { data, loading } = useGetGroups({
    variables: {
      page: page || 1,
      per: 20,
      q: q || undefined,
      sortBy: (sortBy as SortableVariables<Group>) || undefined,
      direction: direction as "ASC" | "DESC",
    },
  });
  const groups = data?.groups?.collection || [];
  const itemsCount = data?.groups?.metadata?.totalCount;

  const actions = useMemo(
    () => [
      isCapableOfInsertingGroups && (
        <Button
          key="groupInsertButton"
          id="groupInsertButton"
          onClick={() => setNewGroupDialog(true)}
        >
          {t("groups.add")}
        </Button>
      ),
    ],
    []
  );

  return (
    <ScreenContainer
      error={
        !isCapableOfReadingGroups ? (
          <EmptyState
            title={t("network.unauthorized") as string}
            action={
              <Link to="/" variant="button">
                {t("network.backToHome")}
              </Link>
            }
          />
        ) : undefined
      }
    >
      <ScreenHeader
        title={
          <span className="flex items-center space-x-4">
            {t("groups.list")}
          </span>
        }
        actions={actions}
        description={t("groups.listDescription")}
        icon={GroupIcon}
      />
      <Container>
        <div className="flex flex-col py-5">
          <div className="flex flex-col py-4 space-y-4">
            <>
              <List
                loading={loading}
                data={groups}
                listOnly
                renderAsList={(data) => (
                  <GroupItem
                    group={data}
                    key={data.id}
                    setNewGroupDialog={setNewGroupDialog}
                    deleteGroup={deleteGroup}
                  />
                )}
                renderEmptyState={() => {
                  return <H6>{t("groups.noMatchingGroups")}</H6>;
                }}
                id="users-list"
              >
                <SearchInput
                  defaultValue={q}
                  onChange={(q) => setQuery({ page: 1, q })}
                  label={t("list.search") as string}
                  placeholder={t("groups.searchPlaceholder") as string}
                />
                <div className="flex space-x-3">
                  <ButtonGroup scale="sm">
                    <Button
                      icon={SortAscendingDirectionIcon}
                      onClick={() => setQuery({ direction: "ASC" })}
                      variant={
                        direction === "ASC" || !direction
                          ? "primary"
                          : undefined
                      }
                    />
                    <Button
                      icon={SortDescendingDirectionIcon}
                      onClick={() => setQuery({ direction: "DESC" })}
                      variant={direction === "DESC" ? "primary" : undefined}
                    />
                  </ButtonGroup>
                </div>
              </List>
              {!loading && (
                <Pagination itemsCount={itemsCount} itemsPerPage={20} />
              )}
            </>
          </div>
        </div>
      </Container>
      <InsertGroupDialog
        group={newGroupDialog}
        onClose={() => setNewGroupDialog(undefined)}
      />
    </ScreenContainer>
  );
};

export default React.memo(GroupList);
